import React, { useState } from "react";
import { toast } from "react-toastify";
import NumberField from "../../../Components/NumberField";
import Handler from "../../../Helpers/Handler";
import { firstError, fuelRowsErrors, invalidClass, odometerError, RULES } from "../../../Helpers/bookingRules";
import { currencyFormatter } from "../../../Helpers/Utils";
import tripUrls from "./tripUrls";

// Complete trip = the end readings only (Booking Flow v2, step 2 of ending a trip; the booking must be
// Ended and the pick-up done). "Check" asks the server what the readings record (read-only preview):
// km over the allowance, fuel short and FASTag shortfall - details, no amounts. "Complete trip" saves
// them and moves the booking Ended -> Completed. The late hours were fixed when the trip was Ended.
// The charges are the next step: Fine charges (Calculate, then Submit).

const ExcessSummary = ({ excess }) => {
    const rows = [
        { label: "Late return", value: Number(excess.hours) > 0 ? `${excess.hours} h (${excess.lateMinutes} min late, ${excess.graceMinutes} min grace)` : excess.lateMinutes > 0 ? `None (${excess.lateMinutes} min late, within the grace)` : "None" },
        { label: "Km over the allowance", value: Number(excess.km) > 0 ? `${excess.km} km (${excess.usedKm} used of ${excess.kmAllowance})` : `None (${excess.usedKm ?? 0} of ${excess.kmAllowance ?? 0} km used)` },
        { label: "Fuel short", value: excess.fuel?.length ? excess.fuel.map((f) => `${f.fuelType} ${f.percentage}%`).join(", ") : "None" },
        { label: "FASTag shortfall", value: Number(excess.fastag) > 0 ? currencyFormatter(excess.fastag) : "None" },
    ];
    return (
        <div className="border rounded-3 p-3 mb-2">
            {rows.map((row) => (
                <div key={row.label} className="d-flex justify-content-between small mb-1 gap-3">
                    <span className="text-muted">{row.label}</span>
                    <span className="text-end">{row.value}</span>
                </div>
            ))}
        </div>
    );
};

const CompleteTripModal = ({ bookingId, fuelTypes, startKm, onClose, onDone, onFines }) => {
    const [endKm, setEndKm] = useState("");
    const [endFuel, setEndFuel] = useState(() =>
        (fuelTypes?.length ? fuelTypes : ["Fuel"]).map((ft) => ({ fuelType: ft, percentage: "" }))
    );
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [excess, setExcess] = useState(null); // the preview, shown before Complete trip
    const [done, setDone] = useState(false);

    const fuelPayload = () => endFuel.map((f) => ({ fuelType: f.fuelType, percentage: Number(f.percentage) }));
    const updateFuel = (idx, value) => {
        setEndFuel((prev) => prev.map((f, i) => (i === idx ? { ...f, percentage: value } : f)));
        setErrors((prev) => ({ ...prev, fuel: { ...(prev.fuel || {}), [idx]: null } }));
        setExcess(null);
    };

    const check = async () => {
        const found = {
            endKm: odometerError(endKm, { label: "End odometer", floor: startKm, floorLabel: "the start reading" }),
            fuel: fuelRowsErrors(endFuel),
        };
        setErrors(found);
        const problem = firstError(found);
        if (problem) { toast.error(problem); return; }
        setBusy(true);
        const response = await Handler({ method: "post", url: tripUrls.completePreview(bookingId), data: { endKm: Number(endKm), endFuel: fuelPayload() } });
        setBusy(false);
        if (!response?.success || !response?.data?.data?.excess) {
            toast.error(response?.data?.message || "Could not check these readings");
            return;
        }
        setExcess(response.data.data.excess);
    };

    const complete = async () => {
        setBusy(true);
        const response = await Handler({ method: "patch", url: tripUrls.complete(bookingId), data: { endKm: Number(endKm), endFuel: fuelPayload() } });
        setBusy(false);
        if (response?.success) {
            toast.success("Trip completed. Next: Fine charges");
            setDone(true);
            onDone?.();
        } else {
            toast.error(response?.data?.message || "Could not complete this trip");
        }
    };

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Complete Trip</h5>
                            <button type="button" className="btn-close" onClick={onClose} disabled={busy}></button>
                        </div>
                        <div className="modal-body">
                            {done ? (
                                <div className="alert alert-success py-2 px-3 small mb-0">
                                    Trip completed. The readings are saved. Enter the fine charges next to close the booking (or raise the balance due).
                                </div>
                            ) : (
                                <>
                                    <div className="small text-muted mb-3">Enter the readings at hand-back. Check them to see what is recorded, then complete the trip. The charges are entered in the next step.</div>
                                    <div className="mb-3">
                                        <label className="form-label small">End odometer (km)</label>
                                        <NumberField
                                            min={startKm ?? 0}
                                            max={RULES.ODOMETER_MAX}
                                            className={`form-control${invalidClass(errors.endKm)}`}
                                            value={endKm}
                                            onChange={(v) => { setEndKm(v); setErrors((prev) => ({ ...prev, endKm: null })); setExcess(null); }}
                                            disabled={busy}
                                            aria-invalid={!!errors.endKm}
                                        />
                                        {errors.endKm && <div className="invalid-feedback">{errors.endKm}</div>}
                                        {startKm !== undefined && startKm !== null && (
                                            <div className="form-text">Started this trip at {startKm} km - end reading can't be lower than this.</div>
                                        )}
                                    </div>

                                    <div className="fw-semibold small mb-2">End fuel level</div>
                                    <div className="row g-2 mb-3">
                                        {endFuel.map((f, i) => (
                                            <div className="col-6" key={f.fuelType + i}>
                                                <label className="form-label small"><span className="text-capitalize">{f.fuelType}</span> % <span className="text-muted">(0 to 100)</span></label>
                                                <NumberField
                                                    min={0}
                                                    max={100}
                                                    className={`form-control${invalidClass(errors.fuel?.[i])}`}
                                                    value={f.percentage}
                                                    onChange={(v) => updateFuel(i, v)}
                                                    disabled={busy}
                                                    aria-invalid={!!errors.fuel?.[i]}
                                                />
                                                {errors.fuel?.[i] && <div className="invalid-feedback">{errors.fuel[i]}</div>}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="form-text mb-3">FASTag balance is fetched automatically from Surepass - it doesn't need to be entered here.</div>
                                    {excess && <ExcessSummary excess={excess} />}
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>{done ? "Close" : "Cancel"}</button>
                            {done
                                ? (onFines && <button type="button" className="btn btn-primary" onClick={onFines}>Next: Fine charges</button>)
                                : excess
                                    ? <button type="button" className="btn btn-success" onClick={complete} disabled={busy}>{busy ? "Completing..." : "Complete Trip"}</button>
                                    : <button type="button" className="btn btn-primary" onClick={check} disabled={busy}>{busy ? "Checking..." : "Check Readings"}</button>}
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

export default CompleteTripModal;
